import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, Link } from 'react-router-dom';
import { aiAPI, locationAPI } from '../lib/api';
import AreaIntelligencePanel from '../components/areaIntelligence/AreaIntelligencePanel';
import { resolveAreaIntelligence } from '../lib/areaIntelligence/config';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import FormattedMessage from '../components/FormattedMessage';
import { translateTemplate } from '../lib/publicListingTruth';
import {
  MessageSquare,
  Send,
  Loader2,
  Bot,
  User,
  MapPin,
  Settings,
  Sliders,
  Lock,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GUEST_MESSAGE_LIMIT = 5;

function buildInitialGreeting(t, isAuthenticated) {
  const main = t(
    'concierge.initial_greeting',
    `Hey there! 👋 I'm PCS Buddy, your personal AI assistant for relocating to Europe.\n\nI'm here to help you explore your new duty station — from the big picture down to the details:\n\n🗺️ Location Overview:\n• Which base are you PCSing to?\n• What's the surrounding area like?\n\n🏘️ Neighborhoods:\n• Best areas for families vs singles\n• Distance from base, schools, amenities\n• Housing market & typical rents\n\n🎯 Local Details:\n• Schools (DoDEA & German options)\n• Shopping, restaurants, activities\n• Healthcare, pharmacies, banks\n• German customs & tips\n\nStart by telling me your duty station (e.g., "I'm PCSing to Ramstein") and I'll guide you through everything!`
  );
  const guestSuffix = !isAuthenticated
    ? translateTemplate(
        t('concierge.initial_greeting_guest_suffix', 'Try me free! You have {limit} free messages as a guest.'),
        { limit: GUEST_MESSAGE_LIMIT },
      )
    : '';
  return {
    role: 'assistant',
    isDefaultGreeting: true,
    content: guestSuffix ? `${main}\n\n${guestSuffix}` : main,
  };
}

export default function ConciergePage() {
  const { user, isAuthenticated } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const lastAssistantMsgRef = useRef(null);
  const prevMessageCount = useRef(1);
  const autoSubmitRanRef = useRef(false);
  const [locations, setLocations] = useState([]);
  const [guestId, setGuestId] = useState(() => localStorage.getItem('guest_ai_id') || null);
  const [guestMessagesUsed, setGuestMessagesUsed] = useState(0);
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const [messages, setMessages] = useState(() => [buildInitialGreeting(t, isAuthenticated)]);
  const [input, setInput] = useState('');
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('general');
  const [areaPanelOpen, setAreaPanelOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [aiSettings, setAiSettings] = useState({
    responseStyle: 'balanced',
    focusArea: 'general',
    language: 'en',
  });

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pcs_buddy_prefill');
      if (!raw) return;
      const { message, autoSubmit } = JSON.parse(raw);
      localStorage.removeItem('pcs_buddy_prefill');
      if (!message) return;
      if (autoSubmit) {
        setPendingAutoSubmit(message);
      } else {
        setInput(message);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      scrollForNewMessage(messages[messages.length - 1]);
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  // Rebuild the default greeting when locale or auth state changes.
  // Only replaces if no real conversation has started yet.
  useEffect(() => {
    setMessages(prev =>
      prev.length === 1 && prev[0].isDefaultGreeting
        ? [buildInitialGreeting(t, isAuthenticated)]
        : prev
    );
  }, [locale, isAuthenticated, t]); // t must be included: it gets a new reference when async translations load

  const loadLocations = async () => {
    try {
      const response = await locationAPI.getAll({ active_only: true });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const scrollForNewMessage = (msg) => {
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (!container) return;
      if (msg.role === 'user') {
        container.scrollTop = container.scrollHeight;
      } else {
        const el = lastAssistantMsgRef.current;
        if (el) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          container.scrollTop += elRect.top - containerRect.top - 12;
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
    });
  };

  const submitMessage = useCallback(async (messageOverride) => {
    const userMessage = String(messageOverride || '').trim();
    if (!userMessage || loading) return;
    if (!isAuthenticated && guestLimitReached) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      if (isAuthenticated) {
        const context = {
          responseStyle: aiSettings.responseStyle,
          focusArea: aiSettings.focusArea,
          tuning: {
            detail_level: aiSettings.responseStyle === 'concise' ? 'brief' : aiSettings.responseStyle === 'detailed' ? 'comprehensive' : 'moderate',
            focus_interests: aiSettings.focusArea !== 'general' ? [aiSettings.focusArea] : [],
          }
        };

        const response = await aiAPI.concierge({
          message: userMessage,
          location_id: selectedLocation === 'general' ? null : selectedLocation,
          language: locale,
          context,
        });

        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: response.data.response, provider: response.data.provider },
        ]);
      } else {
        const guestParam = guestId ? `?guest_id=${guestId}` : '';
        const response = await fetch(`${API_URL}/api/ai/concierge-guest${guestParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMessage,
            location_id: selectedLocation === 'general' ? null : selectedLocation,
            language: locale,
          })
        });

        const data = await response.json();

        if (data.guest_id && !guestId) {
          setGuestId(data.guest_id);
          localStorage.setItem('guest_ai_id', data.guest_id);
        }

        if (data.message_count !== undefined) {
          setGuestMessagesUsed(data.message_count);
        }

        if (data.limit_reached) {
          setGuestLimitReached(true);
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: data.prompt || t('concierge.guest_limit_reached', "You've used all your free messages! Sign up for unlimited access to PCS Buddy."),
              isLimitReached: true
            },
          ]);
        } else if (data.response) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: data.response,
              remaining: data.remaining
            },
          ]);
        }
      }
    } catch (error) {
      console.error('AI Error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: t('concierge.error_message', 'I apologize, but I encountered an error. Please try again or rephrase your question.'),
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [aiSettings, selectedLocation, isAuthenticated, guestLimitReached, guestId, loading, locale]);

  useEffect(() => {
    if (!pendingAutoSubmit || autoSubmitRanRef.current || loading) return;
    autoSubmitRanRef.current = true;
    setPendingAutoSubmit(null);
    submitMessage(pendingAutoSubmit);
  }, [pendingAutoSubmit, loading, submitMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitMessage(input);
  };

  const quickPrompts = [
    t('concierge.quick_prompt_1', 'What are the best neighborhoods near Ramstein?'),
    t('concierge.quick_prompt_2', 'Tell me about German grocery stores'),
    t('concierge.quick_prompt_3', 'What should I know about German schools?'),
    t('concierge.quick_prompt_4', 'Best weekend trips from Stuttgart'),
  ];

  return (
    <div className="bg-ivory pt-20" data-testid="concierge-page">
      <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-signal-blue to-military-gold rounded-lg flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-800">{t('concierge.page_title', 'PCS BUDDY')}</h1>
              <p className="text-xs text-slate-800">{t('concierge.ai_subtitle', 'Your AI relocation assistant')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-[160px] bg-white border-slate-300 text-slate-800 text-sm" data-testid="location-select">
                <MapPin className="w-3 h-3 mr-1 text-slate-800" />
                <SelectValue placeholder={t('concierge.location_placeholder', 'Location')} />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                <SelectItem value="general">{t('concierge.location_general', 'General')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id} className="text-slate-700">
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {resolveAreaIntelligence().enabled && selectedLocation !== 'general' && (
              <button
                onClick={() => setAreaPanelOpen(true)}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-champagne-100 hover:bg-champagne-200 border border-champagne-200 rounded-full transition-colors"
                data-testid="explore-area-btn"
              >
                <MapPin className="w-3.5 h-3.5 text-military-gold" />
                {t('concierge.explore_area', 'Explore this area')}
              </button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className={`text-slate-600 hover:text-slate-800 ${showSettings ? 'bg-slate-200' : ''}`}
              data-testid="ai-settings-toggle"
            >
              <Sliders className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {showSettings && (
          <div className="p-4 bg-white/50 border-b border-slate-200 animate-fade-in" data-testid="ai-settings-panel">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-military-gold" />
              <span className="text-sm font-medium text-slate-700">{t('concierge.ai_tuning', 'AI Tuning Options')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label-tactical mb-1.5 block">{t('concierge.response_style', 'Response Style')}</label>
                <Select value={aiSettings.responseStyle} onValueChange={(v) => setAiSettings({...aiSettings, responseStyle: v})}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-800 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    <SelectItem value="concise" className="text-slate-700">{t('concierge.style_concise', 'Concise')}</SelectItem>
                    <SelectItem value="balanced" className="text-slate-700">{t('concierge.style_balanced', 'Balanced')}</SelectItem>
                    <SelectItem value="detailed" className="text-slate-700">{t('concierge.style_detailed', 'Detailed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="label-tactical mb-1.5 block">{t('concierge.focus_area', 'Focus Area')}</label>
                <Select value={aiSettings.focusArea} onValueChange={(v) => setAiSettings({...aiSettings, focusArea: v})}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-800 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    <SelectItem value="general" className="text-slate-700">{t('concierge.focus_general', 'General')}</SelectItem>
                    <SelectItem value="family" className="text-slate-700">{t('concierge.focus_family', 'Family-Focused')}</SelectItem>
                    <SelectItem value="outdoors" className="text-slate-700">{t('concierge.focus_outdoors', 'Outdoor Activities')}</SelectItem>
                    <SelectItem value="nightlife" className="text-slate-700">{t('concierge.focus_nightlife', 'Nightlife & Dining')}</SelectItem>
                    <SelectItem value="culture" className="text-slate-700">{t('concierge.focus_culture', 'Culture & History')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="label-tactical mb-1.5 block">{t('concierge.creativity', 'Creativity')}</label>
                <div className="flex items-center gap-2 h-9 px-3 bg-white border border-slate-300 rounded-sm">
                  <span className="text-xs text-slate-800">{t('concierge.precision', 'Precise')}</span>
                  <div className="flex-1 h-1 bg-slate-700 rounded-full relative">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-signal-blue rounded-full" />
                  </div>
                  <span className="text-xs text-slate-800">{t('concierge.creative', 'Creative')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              ref={msg.role === 'assistant' && i === messages.length - 1 ? lastAssistantMsgRef : null}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 bg-signal-blue/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-signal-blue" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-signal-blue text-white p-4'
                    : msg.isError
                    ? 'bg-red-500/10 border border-red-500/30 text-red-300 p-4'
                    : msg.isLimitReached
                    ? 'bg-military-gold/10 border border-military-gold/30 p-4'
                    : 'bg-white border border-champagne-200 text-slate-700 shadow-sm p-5'
                }`}
              >
                <div className={`${msg.role === 'assistant' ? 'prose prose-sm prose-slate max-w-none' : ''}`}>
                  <FormattedMessage content={msg.content} />
                </div>

                {msg.image && (
                  <div className="mt-4 rounded-lg overflow-hidden border border-slate-300">
                    <img src={msg.image} alt={t('concierge.related_visual_alt', 'Related visual')} className="w-full max-h-64 object-cover" />
                    {msg.imageCaption && (
                      <p className="text-xs text-slate-800 p-2 bg-white/50">{msg.imageCaption}</p>
                    )}
                  </div>
                )}

                {msg.provider && (
                  <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-600/50 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {t('concierge.powered_by', 'Powered by')} {msg.provider}
                  </p>
                )}

                {msg.remaining !== undefined && msg.remaining > 0 && (
                  <p className="text-xs text-slate-800 mt-2">
                    {msg.remaining} {t('concierge.free_messages_remaining', 'free messages remaining')}
                  </p>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-slate-300" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-signal-blue/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-signal-blue" />
              </div>
              <div className="bg-white border border-champagne-200 shadow-sm p-4 rounded-xl">
                <Loader2 className="w-5 h-5 text-signal-blue animate-spin" />
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-4 py-2 border-t border-slate-200">
            <p className="text-xs text-slate-800 mb-2">{t('concierge.quick_questions', 'Quick questions:')}</p>
            <div className="flex flex-wrap gap-2">
              {quickPrompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setInput(prompt)}
                  className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 hover:text-slate-900 text-xs rounded-sm transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isAuthenticated && !guestLimitReached && guestMessagesUsed > 0 && (
          <div className="px-4 py-2 border-t border-slate-200 bg-white/50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-800">
                {t('concierge.free_messages_label', 'Free messages:')} {GUEST_MESSAGE_LIMIT - guestMessagesUsed} {t('concierge.remaining', 'remaining')}
              </span>
              <Link to="/register" className="text-xs text-signal-blue hover:underline">
                {t('concierge.sign_up_unlimited', 'Sign up for unlimited')}
              </Link>
            </div>
          </div>
        )}

        {!isAuthenticated && guestLimitReached && (
          <div className="px-4 py-3 border-t border-slate-200 bg-military-gold/10">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-military-gold flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-200">{t('concierge.free_trial_ended', 'Free trial ended')}</p>
                <p className="text-xs text-slate-600">{t('concierge.sign_up_continue', 'Sign up to continue using PCS Buddy')}</p>
              </div>
              <Link to="/register">
                <Button size="sm" className="bg-military-gold text-slate-950 hover:bg-military-gold/90">
                  {t('concierge.sign_up_free', 'Sign Up Free')}
                </Button>
              </Link>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-4 pt-4 pb-6 border-t border-slate-200">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                guestLimitReached
                  ? t('concierge.placeholder_limited', 'Sign up to continue chatting...')
                  : isAuthenticated
                    ? t('concierge.placeholder_auth', 'Ask me anything about Germany...')
                    : t('concierge.placeholder_guest', 'Try asking me anything...')
              }
              disabled={loading || guestLimitReached}
              className="flex-1 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim() || guestLimitReached}
              className="bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm px-4"
              data-testid="send-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
          {!isAuthenticated && !guestLimitReached && (
            <p className="text-xs text-slate-800 mt-2 text-center">
              <button
                type="button"
                onClick={() => navigate('/register?redirect=/concierge')}
                className="text-signal-blue hover:underline"
              >
                {t('concierge.sign_up', 'Sign up')}
              </button>{' '}
              {t('concierge.sign_up_suffix', 'for unlimited PCS Buddy access')}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-3 text-center leading-relaxed">
            {t('concierge.disclaimer', 'PCS Buddy uses AI and may be inaccurate. Always verify housing, TLA eligibility, and relocation details with official sources. This is not legal, financial, or professional advice. Conversations may be logged to improve the service.')}{' '}
            <Link to="/privacy" className="underline hover:text-slate-600">
              {t('concierge.disclaimer_privacy_link', 'See our Privacy Policy.')}
            </Link>
          </p>
        </form>
      </div>

      <AreaIntelligencePanel
        location={locations.find((loc) => loc.id === selectedLocation) || null}
        open={areaPanelOpen}
        onClose={() => setAreaPanelOpen(false)}
      />
    </div>
  );
}
